/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import type { JSX } from 'react';
import { UtrvStatus } from '@constants/status';
import { UtrvAssuranceStatus } from '@g17eco/types/universalTrackerValue';
import { PackOption } from '@constants/standards-frameworks';
import { DownloadUtrvStatusCombined } from '@g17eco/types/common';
import { SurveyScope, ScopeGroups } from '@g17eco/types/surveyCommon';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { AppConfig } from '@g17eco/types/app';

export interface SelectedGroup extends Pick<MetricGroup, 'universalTrackers'> {
  code: string
  name: string
  checked: boolean;
  type: ScopeGroups;
}

export interface DownloadSettingsType extends DownloadDisplayOptions {
  selectedScopes: SelectedGroup[];
  visibility: VisibilityStatus;
  status?: DownloadUtrvStatus;
  assuranceStatus?: DownloadUtrvStatus;
}

export interface OptionTitle {
  title?: string,
  tooltip?: string
}

export type DownloadSettingsChange =
  | { key: 'scope'; value: string[] }
  | {
  key: 'status' | 'assuranceStatus' | 'privacy' | 'display';
  value: string;
};

export interface DownloadSettingPropType {
  isOpen: boolean;
  isDisabled?: boolean;
  title: string | React.JSX.Element;
  onDownload: HandleDownload;
  handleClose: () => void;
  handleChange?: (change: DownloadSettingsChange) => void;
  downloadSettings?: DownloadSettingsType;
  code?: string;
  requiredScope?: string;
  titleOptions?: {
    word?: OptionTitle;
    simple?: OptionTitle;
    detailed?: OptionTitle;
    scopes?: OptionTitle;
  };
  totals?: ScopeTotals;
  disabledOptions?: string[];
  defaultDownloadOptions?: AppConfig['settings']['defaultDownloadOptions'];
  initiativeId?: string;
  portfolioId?: string;
}

export enum DownloadType {
  Docx = 'docx',
  Xlsx = 'xlsx',
  Pptx = 'pptx',
  Csv = 'csv',
  Xhtml = 'xhtml',
}

export interface HandleDownloadConfig extends Partial<Pick<PackOption, 'scopeType'>> {
  packCode?: string;
  debug?: boolean;
  scheme?: string;
  aiEnhanced?: boolean;
}

export type HandleDownload = (type: DownloadType, config?: HandleDownloadConfig) => Promise<void>;

export enum VisibilityStatus {
  /** Show all available data **/
  Include = 'include',
  /** Exclude data, no utrv's returned **/
  ExcludeData = 'exclude_data',
  /**
   * Remove value, valueData, note properties only
   * Required to generated Word docs, but explicitly handle private values
   **/
  ExcludeValuesOnly = 'exclude_values',
}

export interface DownloadDisplayOptions {
  displayMetricOverrides?: boolean;
  displayUserInput?: boolean;
  displayTag?: boolean;
}

export type DownloadScopeType = Omit<SurveyScope, ScopeGroups.Materiality>;

export interface DownloadMultiScope extends DownloadDisplayOptions {
  statuses?: UtrvStatus[];
  assuranceStatus?: UtrvAssuranceStatus[];
  visibility?: VisibilityStatus;
  scope: DownloadScopeType;
  targets?: boolean;
}

interface DownloadOption {
  handler: () => Promise<void>;
  icon?: string;
  type: string;
}

export interface DownloadGroup {
  id: 'word' | 'simple' | 'detailed' | 'sdg' | 'pptx' | 'xhtml';
  title: string | JSX.Element;
  tooltip: string;
  options: DownloadOption[];
  hidden?: boolean;
}

export interface ScopeTotals {
  total: number;
  verified: number;
  sentToAssurer: number;
  assured: number;
  restated: number;
}

export interface CardsTotals {
  [key: string]: ScopeTotals;
}

export enum PPTXTemplateScheme {
  Default = 'default',
  // hexagons
  Aspect = 'aspect',
  Blocks = 'blocks',
  Green = 'green',
  Office = 'office',
  Paper = 'paper',
  Slipstream = 'slipstream',
}

export const PPTXTemplateSchemeLabelMap: { [key in PPTXTemplateScheme]: string } = {
  [PPTXTemplateScheme.Default]: 'Minimalist',
  [PPTXTemplateScheme.Aspect]: 'Hexagons',
  [PPTXTemplateScheme.Blocks]: 'Blocks',
  [PPTXTemplateScheme.Green]: 'Green',
  [PPTXTemplateScheme.Office]: 'Office',
  [PPTXTemplateScheme.Paper]: 'Paper',
  [PPTXTemplateScheme.Slipstream]: 'Slipstream',
};

export enum PPTXColorScheme {
  Ocean = 'ocean',
  Sunset = 'sunset',
  Valley = 'valley',
}

export const PPTXColorSchemeMap: { [key in PPTXColorScheme]: string[] } = {
  [PPTXColorScheme.Ocean]: [
    '#26282B',
    '#84868A',
    '#4F89BC',
    '#5B9BD5',
    '#2970C2',
    '#2C9FD5',
    '#30D2D8',
    '#F5F5F1',
    '#FFFFFF',
  ],
  [PPTXColorScheme.Sunset]: [
    '#002060',
    '#84868A',
    '#2F5597',
    '#F65751',
    '#128D91',
    '#4A82E3',
    '#FF7E79',
    '#D6DCE5',
    '#FFFFFF',
  ],
  [PPTXColorScheme.Valley]: [
    '#0B213F',
    '#0B213F',
    '#38761D',
    '#0B5394',
    '#BF9000',
    '#85200C',
    '#351C75',
    '#093607',
    '#FFFCE9',
  ],
};

export interface PPTXReportScheme {
  template: PPTXTemplateScheme;
  color: PPTXColorScheme;
}

export type DownloadUtrvStatus = UtrvStatus | DownloadUtrvStatusCombined

export interface ISSBAIContent {
  type: 'issb';
  executiveSummary: string;
  nextSteps: Array<{ title: string; explanation: string }>;
}

export type AIContent = ISSBAIContent;
